import type { TaskStepState } from '@/features/contribution-kit';
import type { TranscriptionOutcome, ValidationAudioItem } from './types';

/**
 * Non-visual rules of the "Valider des textes" session, kept pure so tests
 * lock the payloads sent to transcriptionApi and the batch transitions.
 * Moved as is from use-validate-transcription-session.
 */

/** One batch, always page 1 (the backend hands a fresh random batch). */
export const VALIDATE_TRANSCRIPTION_PAGE_SIZE = 10;

/** Rejection reasons sent as is to the API (unchanged codes) and their t keys. */
export const TRANSCRIPTION_REJECTION_REASONS = [
  ['transcription_quality_poor', 'transcriptionQualityPoor'],
  ['transcription_does_not_match_audio', 'transcriptionDoesNotMatchAudio'],
  ['transcription_incomplete', 'transcriptionIncomplete'],
  ['transcription_contains_errors', 'transcriptionContainsErrors'],
  ['other', 'other'],
] as const;

type RawItem = Record<string, unknown>;

/**
 * One API item to a screen item. The proposal wins; without one, a random
 * transcription of the list is shown (`random` is Math.random in the app).
 */
export function normalizeTranscriptionItem(it: RawItem, random: () => number = Math.random): ValidationAudioItem {
  let activeText = typeof it?.transcriptionProposal === 'string' ? it.transcriptionProposal : '';
  if (!activeText && Array.isArray(it?.transcriptions) && (it.transcriptions as RawItem[]).length > 0) {
    const list = it.transcriptions as RawItem[];
    const randomIndex = Math.floor(random() * list.length);
    activeText = String(list[randomIndex]?.text ?? '');
  }

  return {
    id: String(it?.id ?? it?.audioId ?? it?._id ?? ''),
    text: activeText || '',
    transcriptions: (it?.transcriptions as Array<{ text?: string }>) || [],
    audioUrl: String(it?.url ?? it?.audioUrl ?? ''),
    transcriptionId: typeof it?.transcriptionId === 'string' ? it.transcriptionId : null,
    langFr: typeof it?.langFr === 'string' ? it.langFr.trim() : '',
    phraseFr: typeof it?.phraseFr === 'string' ? it.phraseFr.trim() : '',
    validated: Boolean(it?.validated),
    isInitialAnswer: it?.source === 'initialAudio',
  };
}

/**
 * The batch returned by getAudiosWithRandomTranscription, whatever envelope
 * it came in, without the items missing an id or an audio. A non-array body
 * throws, as before, so the caller shows its loading error.
 */
export function normalizeTranscriptionBatch(res: unknown, random: () => number = Math.random): ValidationAudioItem[] {
  const r = res as { data?: { data?: unknown } } | null | undefined;
  const list = r?.data?.data ?? r?.data ?? res;
  return ((list || []) as RawItem[])
    .map((it) => normalizeTranscriptionItem(it, random))
    .filter((item) => item.id && item.audioUrl);
}

/** Validate keeps the text as it stands in the editor (corrected or not). */
export const validatePayload = (item: ValidationAudioItem, text: string) => ({
  audioId: item.transcriptionId || item.id,
  transcriptionText: text,
});

/** Reject: the detailed text wins over the picked reason when filled. */
export const rejectPayload = (item: ValidationAudioItem, text: string, rejectionReason: string, detailedReason: string) => ({
  audioId: item.transcriptionId || item.id,
  transcriptionText: text,
  action: 'reject' as const,
  reason: detailedReason.trim() || rejectionReason,
});

/** Finalize targets the audio id, not the transcription id (unchanged). */
export const finalizePayload = (item: ValidationAudioItem, text: string) => ({ audioId: item.id, text });

/** Marks the item at `index` as handled, with what was done. */
export function markHandled(items: ValidationAudioItem[], index: number, outcome: TranscriptionOutcome): ValidationAudioItem[] {
  const copy = [...items];
  if (copy[index]) copy[index] = { ...copy[index], validated: true, outcome };
  return copy;
}

/** Next index, or `ended` on the last item (the cursor then stays put). */
export function nextIndex(index: number, length: number): { index: number; ended: boolean } {
  return index < length - 1 ? { index: index + 1, ended: false } : { index, ended: true };
}

export const previousIndex = (index: number) => (index > 0 ? index - 1 : index);

export function transcriptionProgress(items: ValidationAudioItem[]) {
  const validatedCount = items.filter((item) => item.validated).length;
  return { validatedCount, progressPercent: Math.round((validatedCount / Math.max(1, items.length)) * 100) };
}

/** Every item is handled and at least one was handled in this session. */
export const isBatchComplete = (items: ValidationAudioItem[]) =>
  items.length > 0 && items.some((item) => item.outcome) && items.every((item) => item.outcome || item.validated);

export function outcomeCounts(items: ValidationAudioItem[]) {
  const count = (outcome: TranscriptionOutcome) => items.filter((item) => item.outcome === outcome).length;
  return { validated: count('validated'), rejected: count('rejected'), finalized: count('finalized') };
}

/** Step dots state of each item: rejected in red, any other handled item done. */
export const stepOutcome = (item: ValidationAudioItem | undefined): Exclude<TaskStepState, 'todo' | 'current'> | null =>
  item?.outcome === 'rejected' ? 'rejected' : item?.validated ? 'done' : null;

/** Toast text of a failed validate or reject: the server text, cut at 300. */
export function decisionErrorDescription(err: unknown, fallback: string): string {
  const axiosErr = err as { response?: { data?: unknown }; message?: string };
  const serverData = axiosErr?.response?.data;
  const serverMsg = typeof serverData === 'string' ? serverData : axiosErr?.message || fallback;
  return typeof serverMsg === 'string' && serverMsg.length > 300 ? `${serverMsg.slice(0, 300)}...` : String(serverMsg);
}

/** Toast text of a failed finalize: the server message field, else the error. */
export function finalizeErrorDescription(err: unknown, fallback: string): string {
  const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
  return axiosErr?.response?.data?.message || axiosErr?.message || fallback;
}

/** MIME type of the <source>, from the file extension (unchanged). */
export const audioMimeType = (url: string) =>
  url.endsWith('.webm') ? 'audio/webm' : url.endsWith('.ogg') ? 'audio/ogg' : 'audio/wav';
